import { From } from '@/data/constant'
import { PartType } from '@/partTypes'
import { bujianStoreSourceStrategy } from '@/views/newPaiban/config/storeSourceStrategy'
import {
  IFetchStorePlankDataParams,
  fetchAndNormalizeStorePlankDataByStrategy,
} from '@/views/newPaiban/hooks/useStorePlankData'
import { getBujianItemKey } from '@/views/newPaiban/util/bujianData'
import Vue from 'vue'
import type { Module } from 'vuex'

import type { IRootState } from '../types'

interface QueryCacheItem {
  /** 原始查询结果，保留完整数据，展示时再结合 participation 过滤 */
  data: BujianStoreItem[]
  loading: boolean
  timestamp: number
}

interface VisibleListCacheItem {
  /** 当前 queryKey 对应的可见列表缓存 */
  data: BujianStoreItem[]
  /** participation 集合变化后，旧可见列表缓存必须失效 */
  participationVersion: number
  /** 只有原始查询结果更新时间一致时，可见列表缓存才可复用 */
  queryTimestamp: number
}

export interface BujianStoreItem {
  id?: string | number
  bujianItemKey: string
  plank?: PartType
  _preloadedPlank?: PartType
  render_url?: string
  url: string
  width: number | string | undefined
  height: number | string | undefined
  title: string
  address: string
  partUniqueId: string
  thick: number
  matCode: string
  color: string
  from: From
  plKId: string | number
  plankNum?: string | number
  new?: boolean
  type?: 'local' | 'serve'
  isDone?: 'done' | 'err' | 'load'
  isShow?: boolean
  create_time?: string
  oriData: any
  [key: string]: any
}

export interface BujianStoreState {
  queryCacheMap: Record<string, QueryCacheItem>
  participationBujianPlankIds: string[]
  /** 不直接比较大数组，改用版本号驱动可见列表缓存失效 */
  participationVersion: number
  /** 当前正在拖拽的补件上下文，避免继续复用 awaitPaibanStore.currentDragPlank */
  currentDragBujianPlank: BujianStoreItem | null
  lastQueryKeyByPage: Record<string, string>
}

function getDefaultQueryCacheItem(): QueryCacheItem {
  return {
    data: [],
    loading: false,
    timestamp: 0,
  }
}

const visibleListCacheMap = new Map<string, VisibleListCacheItem>()

let participationSetCache = {
  version: -1,
  value: new Set<string>(),
}

function getParticipationIdSet(state: BujianStoreState) {
  if (participationSetCache.version !== state.participationVersion) {
    participationSetCache = {
      version: state.participationVersion,
      value: new Set(
        state.participationBujianPlankIds.map((item) => String(item))
      ),
    }
  }
  return participationSetCache.value
}

const bujianStore: Module<BujianStoreState, IRootState> = {
  namespaced: true,
  state: {
    queryCacheMap: {},
    participationBujianPlankIds: [],
    participationVersion: 0,
    currentDragBujianPlank: null,
    lastQueryKeyByPage: {},
  },
  getters: {
    getVisibleBujianList: (state) => (queryKey: string) => {
      const queryCache = state.queryCacheMap[queryKey]
      const queryData = queryCache?.data ?? []
      const queryTimestamp = queryCache?.timestamp ?? 0
      const cachedVisibleList = visibleListCacheMap.get(queryKey)

      if (
        cachedVisibleList &&
        cachedVisibleList.queryTimestamp === queryTimestamp &&
        cachedVisibleList.participationVersion === state.participationVersion
      ) {
        return cachedVisibleList.data
      }

      // 原始查询结果和可见列表分开缓存：查询命中缓存时，不再重复做全量过滤。
      const visibleList = queryData.filter((item) => {
        const key = getBujianItemKey(item)
        if (!key) return false
        return !getParticipationIdSet(state).has(key)
      })

      visibleListCacheMap.set(queryKey, {
        data: visibleList,
        queryTimestamp,
        participationVersion: state.participationVersion,
      })

      return visibleList
    },
    getLoadingByQueryKey: (state) => (queryKey: string) => {
      return state.queryCacheMap[queryKey]?.loading ?? false
    },
    getLastQueryKeyByPage: (state) => (pageScope: 'paiban' | 'jinxi') => {
      return state.lastQueryKeyByPage[pageScope] ?? ''
    },
  },
  mutations: {
    setQueryCache(
      state,
      payload: { queryKey: string; data: BujianStoreItem[] }
    ) {
      const { queryKey, data } = payload
      const prev = state.queryCacheMap[queryKey] ?? getDefaultQueryCacheItem()
      Vue.set(state.queryCacheMap, queryKey, {
        ...prev,
        data,
        loading: false,
        timestamp: Date.now(),
      })
    },
    setLoadingByQueryKey(
      state,
      payload: { queryKey: string; loading: boolean }
    ) {
      const { queryKey, loading } = payload
      const prev = state.queryCacheMap[queryKey] ?? getDefaultQueryCacheItem()
      Vue.set(state.queryCacheMap, queryKey, {
        ...prev,
        loading,
      })
    },
    addParticipationBujianPlank(state, val: string | number) {
      const key = String(val)
      if (!key) return
      if (!state.participationBujianPlankIds.includes(key)) {
        state.participationBujianPlankIds.push(key)
        // 所有可见列表都依赖 participation，新增后统一通过版本号失效。
        state.participationVersion++
      }
    },
    removeParticipationBujianPlank(state, val: string | number) {
      const key = String(val)
      const nextIds = state.participationBujianPlankIds.filter(
        (item) => item !== key
      )
      if (nextIds.length !== state.participationBujianPlankIds.length) {
        state.participationBujianPlankIds = nextIds
        // 删除回退后，需要让原来被隐藏的补件重新进入可见列表。
        state.participationVersion++
      }
    },
    syncParticipationBujianPlankIds(state, ids: Array<string | number>) {
      const nextIds = Array.from(
        new Set(ids.map((item) => String(item)).filter(Boolean))
      )
      const isChanged =
        nextIds.length !== state.participationBujianPlankIds.length ||
        nextIds.some(
          (item, index) => item !== state.participationBujianPlankIds[index]
        )
      if (isChanged) {
        state.participationBujianPlankIds = nextIds
        // 整包替换 paibanData 时走这里，同步补件参与状态快照。
        state.participationVersion++
      }
    },
    setCurrentDragBujianPlank(state, val: BujianStoreItem | null) {
      state.currentDragBujianPlank = val
    },
    updateCurrentDragBujianPlank(state, val: PartType) {
      if (state.currentDragBujianPlank) {
        state.currentDragBujianPlank.plank = val
      }
    },
    setLastQueryKeyByPage(
      state,
      payload: { pageScope: 'paiban' | 'jinxi'; queryKey: string }
    ) {
      const { pageScope, queryKey } = payload
      Vue.set(state.lastQueryKeyByPage, pageScope, queryKey)
    },
    clearBujianStoreState(state) {
      state.queryCacheMap = {}
      state.participationBujianPlankIds = []
      state.participationVersion = 0
      state.currentDragBujianPlank = null
      state.lastQueryKeyByPage = {}
      visibleListCacheMap.clear()
    },
  },
  actions: {
    async fetchBujianListByQuery(
      { commit, state },
      payload: {
        queryKey: string
        pageScope: 'paiban' | 'jinxi'
        params: IFetchStorePlankDataParams
        force?: boolean
      }
    ) {
      const { queryKey, pageScope, params, force = false } = payload
      commit('setLastQueryKeyByPage', { pageScope, queryKey })

      if (
        !force &&
        Object.prototype.hasOwnProperty.call(state.queryCacheMap, queryKey)
      ) {
        // participation 已由落板/回退/setPaibanData 维护，这里命中缓存时不再额外扫描 paibanData。
        return state.queryCacheMap[queryKey]?.data ?? []
      }

      commit('setLoadingByQueryKey', { queryKey, loading: true })
      try {
        const data = (await fetchAndNormalizeStorePlankDataByStrategy(
          bujianStoreSourceStrategy,
          params
        )) as BujianStoreItem[]
        const normalizedData = data.map((item) => {
          return {
            ...item,
            bujianItemKey: item.bujianItemKey || getBujianItemKey(item),
          }
        })
        commit('setQueryCache', {
          queryKey,
          data: normalizedData,
        })
        return normalizedData
      } finally {
        commit('setLoadingByQueryKey', { queryKey, loading: false })
      }
    },
  },
}

export default bujianStore
